import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Divider } from '@mui/material';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import { GlassSurface } from './GlassDashboard';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/images/logo.png';

interface DesktopOnlyWrapperProps {
  children: React.ReactNode;
}

const BRAND = '#1976D2';

// Routes that stay reachable on a phone despite the desktop-only gate below.
// The ERP itself is desktop-only, but sign-in must work from mobile — landing on
// the "Optimisé pour ordinateur de bureau" card before you can even authenticate
// reads as "the app is down" rather than "use a bigger screen". It is also the
// only way a Developer can get a session in order to pass the role check.
const MOBILE_ALLOWED_PATHS = ['/login'];

// Exact role name from the `roles` table — NOT checked via `hasRole()`, which
// treats `admin` as holding every role and would therefore let admins in too.
// Mobile access is a developer-testing affordance, so it stays Developer-only.
const MOBILE_ALLOWED_ROLE = 'Developer';

// Trailing slashes and casing vary by how the URL was typed/shared, so compare
// against a normalised form rather than the raw pathname.
const normalizePath = (pathname: string) =>
  pathname.toLowerCase().replace(/\/+$/, '') || '/';

const DesktopOnlyWrapper: React.FC<DesktopOnlyWrapperProps> = ({ children }) => {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsNarrowScreen(window.innerWidth < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Wide screens and the allowlisted routes are never gated, so they don't have
  // to wait on the auth check.
  if (!isNarrowScreen || MOBILE_ALLOWED_PATHS.includes(normalizePath(pathname))) {
    return <>{children}</>;
  }

  // Narrow screen on a gated route: the verdict depends on the role, so hold off
  // until `user` is settled. Rendering either branch early would flash the wrong
  // one — the app for a beat before blocking, or the block before a Developer's
  // session resolves.
  if (loading) {
    return null;
  }

  const isMobile = user?.role !== MOBILE_ALLOWED_ROLE;

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          // Soft, brand-tinted light backdrop — consistent with the glass dashboards.
          background: `
            radial-gradient(85% 60% at 50% 0%, rgba(25,118,210,0.10), transparent 70%),
            radial-gradient(70% 50% at 100% 100%, rgba(0,137,123,0.08), transparent 70%),
            linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%)`,
        }}
      >
        <GlassSurface
          interactive={false}
          accentColor={BRAND}
          sx={{
            width: '100%',
            maxWidth: 420,
            p: { xs: 3.5, sm: 5 },
            textAlign: 'center',
          }}
        >
          {/* Company brand */}
          <Box
            component="img"
            src={logo}
            alt="M.A.F Agencement"
            sx={{ height: 72, width: 'auto', mb: 3 }}
          />

          {/* Single, calm device cue */}
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 3,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: BRAND,
              bgcolor: 'rgba(25,118,210,0.10)',
              border: '1px solid rgba(25,118,210,0.18)',
            }}
          >
            <DesktopWindowsRoundedIcon sx={{ fontSize: 36 }} />
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5, letterSpacing: '-0.01em' }}
          >
            Optimisé pour ordinateur de bureau
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 4 }}
          >
            Pour profiter pleinement de votre espace ERP, connectez-vous depuis un
            ordinateur. L’interface n’est pas conçue pour les écrans mobiles.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography
            variant="caption"
            sx={{
              color: BRAND,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            M.A.F Agencement · ERP
          </Typography>
        </GlassSurface>
      </Box>
    );
  }

  return <>{children}</>;
};

export default DesktopOnlyWrapper;
